import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { RefreshCw, ArrowUpRight, ArrowLeftRight, FileDown, FileText } from "lucide-react";

const MODE_LABEL = { remote_sensing: "Remote Sensing", "remote-sensing": "Remote Sensing", maps: "Geomapping", geophysics: "Geophysics" };
const openRoute = (r) => (r.mode === "geophysics" ? `/geophysics/${r.id}` : r.mode === "maps" ? `/maps/${r.id}` : `/projects/${r.id}`);
const fmtWhen = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

// Super-admin oversight: every user's projects in one searchable table with owner + last-activity,
// one-click Open, Bulk Reassign to another user's folder, and a per-client combined-PDF export.
export function AllUsersProjects() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [reassignTo, setReassignTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [client, setClient] = useState("");
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [briefBusy, setBriefBusy] = useState("");

  const downloadBrief = async (r) => {
    setBriefBusy(r.id);
    const tid = toast.loading("Preparing Command Brief…");
    try {
      const res = await api.get(`/projects/${r.id}/command-brief.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(r.name || "project").replace(/\s+/g, "_")}_Command_Brief.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Command Brief downloaded", { id: tid });
    } catch (e) { toast.error(apiError(e), { id: tid }); }
    finally { setBriefBusy(""); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/projects"); setRows(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const term = q.trim().toLowerCase();
  const visible = rows.filter((r) => !term
    || (r.name || "").toLowerCase().includes(term)
    || (r.created_by || "").toLowerCase().includes(term)
    || (r.client_name || "").toLowerCase().includes(term));
  const clients = [...new Set(rows.map((r) => (r.client_name || "").trim()).filter(Boolean))].sort();
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allChecked = visible.length > 0 && visible.every((r) => sel.includes(r.id));

  const doReassign = async () => {
    if (!reassignTo) return toast.error("Choose a target user");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/projects/reassign", { ids: sel, to_user_id: reassignTo });
      toast.success(`Moved ${data.count} project(s) to ${data.to}`);
      setSel([]); setReassignTo(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doExport = async () => {
    if (!client) return toast.error("Pick a client to export");
    setExporting(true);
    try {
      const res = await api.post("/admin/portfolio-pdf", { client_name: client }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `SYMIN_${client.replace(/[^a-z0-9]+/gi, "_")}_Portfolio.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Portfolio PDF for “${client}” downloaded`);
    } catch (e) {
      toast.error("Export failed - the client needs at least one processed licence");
    } finally { setExporting(false); }
  };

  return (
    <Card className="shadow-sm overflow-hidden" data-testid="all-projects-card" style={{ borderColor: "#E2E8F0" }}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-900">All Users&rsquo; Projects</span>
          <Badge variant="outline" className="border-slate-300 text-slate-600" data-testid="all-projects-count">{visible.length}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={client} onValueChange={setClient}>
            <SelectTrigger className="h-8 w-52 bg-white border-slate-200 text-slate-900 focus:ring-[#C20511]" data-testid="portfolio-client-select">
              <SelectValue placeholder="Client portfolio…" />
            </SelectTrigger>
            <SelectContent>
              {clients.length === 0 ? <SelectItem value="__none" disabled>No clients yet</SelectItem>
                : clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8" style={{ background: "#C20511", color: "#ffffff" }} onClick={doExport}
            disabled={!client || exporting} data-testid="portfolio-export-btn">
            <FileDown size={13} className="mr-1.5" /> {exporting ? "Bundling…" : "Export Portfolio PDF"}
          </Button>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="all-projects-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search project, owner or client…"
              className="h-8 pl-8 w-64 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-[#C20511]" />
          </div>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-600 hover:bg-slate-200" onClick={load} title="Refresh" data-testid="all-projects-refresh"><RefreshCw size={14} /></Button>
        </div>
      </div>

      {sel.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b" style={{ background: "#FDECEC", borderColor: "#F6CBCB" }} data-testid="reassign-bar">
          <ArrowLeftRight size={14} style={{ color: "#92400E" }} />
          <span className="text-xs font-semibold" style={{ color: "#92400E" }}>{sel.length} selected - move to</span>
          <Select value={reassignTo} onValueChange={setReassignTo}>
            <SelectTrigger className="h-8 w-56 bg-white" data-testid="reassign-target-select"><SelectValue placeholder="Choose user…" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8" style={{ background: "#0B1420", color: "#FFFFFF" }} onClick={() => setConfirmOpen(true)} disabled={busy || !reassignTo} data-testid="reassign-apply">
            {busy ? "Moving…" : "Reassign"}
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setSel([])} data-testid="reassign-clear">Clear</Button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[880px]">
          <thead><tr className="border-b border-border text-left">
            <th className="px-4 py-2.5 w-8">
              <input type="checkbox" data-testid="all-projects-select-all" checked={allChecked}
                onChange={(e) => setSel(e.target.checked ? visible.map((r) => r.id) : [])} className="h-4 w-4 accent-[#C20511]" />
            </th>
            {["Project", "Owner", "Client", "Mode", "Areas", "Status", "Last Activity", ""].map((h, i) => (
              <th key={i} className="px-4 py-2.5 overline whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground" data-testid="all-projects-empty">No projects match your search.</td></tr>
            ) : visible.map((r) => (
              <tr key={r.id} className="border-b border-border/60 hover:bg-accent/40 group"
                data-testid={`all-projects-row-${r.id}`}>
                <td className="px-4 py-2.5">
                  <input type="checkbox" data-testid={`all-projects-check-${r.id}`} checked={sel.includes(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 accent-[#C20511]" />
                </td>
                <td className="px-4 py-2.5 font-medium cursor-pointer" onClick={() => nav(openRoute(r))}>
                  {r.name}
                  {r.archived && <Badge variant="outline" className="ml-2 text-[10px]">Archived</Badge>}
                </td>
                <td className="px-4 py-2.5 text-xs" data-testid={`all-projects-owner-${r.id}`}>{r.created_by || "-"}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.client_name || "-"}</td>
                <td className="px-4 py-2.5 text-xs">{MODE_LABEL[r.mode] || "Remote Sensing"}</td>
                <td className="px-4 py-2.5 tabular-nums">{r.area_count}</td>
                <td className="px-4 py-2.5 text-xs">{r.status || "-"}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap" data-testid={`all-projects-activity-${r.id}`}>{fmtWhen(r.updated_at)}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {(!r.mode || String(r.mode).includes("remote")) && (
                      <Button size="sm" variant="outline" className="h-8" disabled={briefBusy === r.id}
                        data-testid={`all-projects-brief-${r.id}`} onClick={() => downloadBrief(r)} title="Download 1-page Command Brief (technical PDF)">
                        <FileText size={13} className="mr-1" /> {briefBusy === r.id ? "…" : "Brief"}
                      </Button>
                    )}
                    <Button size="sm" className="h-8" style={{ background: "#C20511", color: "#FFFFFF" }}
                      data-testid={`all-projects-open-${r.id}`} onClick={() => nav(openRoute(r))}>
                      Open <ArrowUpRight size={13} className="ml-1" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="reassign-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign {sel.length} project{sel.length === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Move {sel.length} project{sel.length === 1 ? "" : "s"} to{" "}
              <b>{(users.find((u) => u.id === reassignTo) || {}).name || (users.find((u) => u.id === reassignTo) || {}).email || "the selected user"}</b>.
              Ownership changes and the licences move into that user&rsquo;s folder. You can reassign them again any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reassign-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmOpen(false); doReassign(); }}
              className="bg-[#0B1420] text-[#FFFFFF] hover:bg-[#0B1420]/90" data-testid="reassign-confirm-apply">
              Move projects
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
